// Joana chat shell — shared domain types. These mirror the Phoenix REST/Channel
// payloads (services/api controllers + services/realtime channels), kept as a
// thin contract so the API client, store, and views agree on shapes. They are
// the wire types, not catalog/render types — the views map these into VNodes.

export interface User {
  id: string;
  username: string;
  display_name: string | null;
  phone?: string | null;
  email?: string | null;
  /** What this person reads POSTS in — a Duniani ranking input, NOT the
   *  language the app is drawn in. */
  primary_language?: string;
  /** ADR-0028 — the language the INTERFACE is drawn in, or null/absent for
   *  "follow my browser". A different question from `primary_language`, in a
   *  different column, for the reason migration 066 gives. */
  ui_language?: string | null;
  status?: string;  avatar?: WireAvatar | null;
  frame?: string | null;
}

// --- Status (D8, ADR-0023) --------------------------------------------------

/** One status, as the tray and the viewer consume it. Ids and sizes, never a
 *  URL: the client composes the fetch URL through `statusMediaUrl`. */
export interface StatusItem {
  id: string;
  author_id: string;
  kind: "text" | "photo" | "video" | "voice" | "mood";
  posted_at: string | null;
  /** When this stops existing. Stamped by the server, never recomputed here. */
  expires_at: string | null;
  caption?: string | null;
  text_bg?: string | null;
  text_font?: string | null;
  mood_id?: string | null;
  mood_glyph?: string | null;
  /** What opening this COSTS. On the wire so the decision can be made before
   *  the bytes are spent (ADR-0023 D11). */
  media_bytes?: number | null;
  thumb_bytes?: number | null;
  duration_ms?: number | null;
  width?: number | null;
  height?: number | null;
  renditions: string[];
  viewed: boolean;
  liked: boolean;
  /** Owner-only, and only in `own`. Nobody else is ever given a count. */
  views?: number | null;
}

/** The author of a tray entry, a post or a comment, projected by the server
 *  through `Avatars.project/2` — so it carries only what THIS viewer may see. */
export interface StatusAuthor {
  user_id: string;
  username: string | null;
  display_name: string | null;
  avatar?: WireAvatar | null;
  frame?: string | null;
}

export interface StatusTrayEntry {
  author_id: string;
  author?: StatusAuthor | null;
  items: StatusItem[];
  latest_at: string | null;
  all_viewed: boolean;
}

export interface StatusViewerRow {
  user_id: string;
  username: string | null;
  display_name: string | null;
  avatar?: WireAvatar | null;
  frame?: string | null;
  viewed_at: string | null;
  liked_at: string | null;
}

/** One surface's privacy settings, plus what that surface OFFERS — from the
 *  server, so the picker cannot present something that would be refused. */
export interface PrivacyAudience {
  surface: string;
  policy: string;
  except: { user_id: string; username: string | null; display_name: string | null }[];
  only: { user_id: string; username: string | null; display_name: string | null }[];
  policies: string[];
  lists: string[];
}

/** A message-preview embedded in a chat-list item (Messaging.build_preview). */
export interface MessagePreview {
  id: string;
  body: string | null;
  user_id: string | null;
  message_type: string;
  created_at: string | null;
  /** Set when the message was deleted for everyone (A10) — it is still the
   *  chat's newest message, so the row says so. */
  deleted_at?: string | null;
  /** What a BODILESS message is. Every media message has an empty `body`, so
   *  `message_type` plus these three are the whole of what the row can say —
   *  without them it read "No messages yet" over a voice note. Optional: an
   *  older backend simply says less. */
  status?: string | null;
  voice?: boolean;
  duration_ms?: number | null;
  /** B9: the card the newest message is, so the row can name the PERSON.
   *  `message_type` alone cannot. */
  contact?: WireContactCard | null;
  /**
   * ADR-0034 D1: this row is a SEALED message's content-free stub — the shape
   * and the timestamp, no words, because the server holds none.
   *
   * Not the same fact as `Chat.e2e`, which is about the conversation. A
   * browser that has decrypted the message overlays its own preview from the
   * message cache and this stops mattering; one that has not draws "Message"
   * rather than "No messages yet", which is what a bodiless stub used to
   * produce.
   *
   * Two states, not three: absent is a server that predates the stub, and a
   * preview it sent in the clear is genuinely not one.
   */
  e2e?: boolean;
  /**
   * ADR-0028: the FACTS behind a system row's sentence, lifted out of
   * `metadata` server-side for the same reason `contact` is — the list has to
   * draw "Umeondoa Neema" and the English `body` cannot say it.
   *
   * The RAW map, as the server lifted it — `parseNoticeMeta` turns it into a
   * `SystemNotice`. Optional: an older server simply yields a row that falls
   * back to `body`, which is what every client did before this existed.
   */
  system?: Record<string, unknown> | null;
}

/** B9 — the triple a contact card carries, exactly what the API whitelists.
 *  Notably NOT a phone number: the server drops that key, and this type has
 *  nowhere to put it (ADR-0031). */
export interface WireContactCard {
  user_id?: string | null;
  username?: string | null;
  display_name?: string | null;
}

/** Somebody's avatar on the wire (ADR-0022): a VERSION for a photo, a ref
 *  for a sticker, a value for an emoji — never a URL. Null/absent = initials,
 *  whether because there is none or because the server decided this viewer
 *  may not see it; the client cannot tell and must not. */
export interface WireAvatar {
  kind: "photo" | "sticker" | "emoji";
  version?: string | null;
  ref?: string | null;
  value?: string | null;
}

/** One counterpart on a chat-list row — a member who is NOT the viewer. */
export interface ChatCounterpart {
  user_id: string;
  username: string | null;
  display_name: string | null;
  avatar?: WireAvatar | null;
  frame?: string | null;
}

/** A chat-list item (Messaging.list_user_chats). */
export interface Chat {
  id: string;
  name: string | null;
  chat_type: string;
  description?: string | null;
  /** ADR-0022: the group icon as a VERSION (never a URL), and who may edit
   *  the group's info. `avatar_url` was the pre-ADR column and is gone. */
  avatar_version?: string | null;
  edit_info_policy?: string | null;
  unread_count: number;
  last_message: MessagePreview | null;
  updated_at?: string | null;
  /** Counterparts, self excluded, server-bounded to 8. A direct chat has no
   *  `name`, so this is what names it. Optional: an older backend omits it. */
  members?: ChatCounterpart[];
  /** Every active member including the viewer. 0/absent when unknown. */
  member_count?: number;
  /**
   * Is this conversation end-to-end encrypted (ADR-0007)?
   *
   * THREE states, like `activeChatE2E` in the store and for the same reason.
   * `undefined` is a server that does not carry the field, and a row that
   * guessed `false` there would tell people their conversation is readable on
   * the strength of a deploy version. It draws nothing.
   */
  e2e?: boolean;
  /**
   * ADR-0018 — the organisation a ZONE belongs to, and the label its header
   * draws. Both absent for everything that is not a zone.
   *
   * The server makes those the same statement: a CHECK ties `chat_type =
   * 'zone'` to `org_id IS NOT NULL`, so a client may trust either and does not
   * have to keep them agreeing.
   */
  org_id?: string | null;
  zone_kind?: string | null;
}

/** A full message row (messaging.messages @derive). */
export interface Message {
  id: string;
  client_msg_id?: string | null;
  chat_id: string;
  user_id: string | null;
  body: string | null;
  message_type: string;
  metadata?: Record<string, unknown>;
  media_url?: string | null;
  media_type?: string | null;
  status?: string;
  reactions?: unknown;
  created_at: string | null;
  updated_at?: string | null;
  /** Client-only: set on an optimistic local echo before the server ack. */
  pending?: boolean;
}

/** POST /api/v1/auth/login single-factor success (Auth.login). */
export interface LoginResponse {
  user: User;
  access_token: string;
  refresh_token: string;
  expires_in: number;
}

/** POST /api/v1/auth/login when a second factor is required. */
export interface TwoFactorChallenge {
  two_factor_required: true;
  challenge_token: string;
  expires_in: number;
}

export type LoginResult = LoginResponse | TwoFactorChallenge;

export function isTwoFactor(r: LoginResult): r is TwoFactorChallenge {
  return (r as TwoFactorChallenge).two_factor_required === true;
}

/** POST /api/v1/auth/web-login — a fresh QR login session. */
export interface WebLoginCreated {
  sid: string;
  /** Returned once; never leaves this tab. Token delivery is bound to it. */
  client_secret: string;
  /** The string encoded in the QR (`wavetalk://weblogin#…`). */
  qr_payload: string;
  /** Server-rendered QR as SVG markup. */
  qr_svg: string;
  expires_in: number;
}

/** POST /api/v1/auth/web-login/poll — browser-side status heartbeat. */
export interface WebLoginPollResult {
  status: "pending" | "scanned" | "approved" | "rejected" | "expired";
  /** Display name of the account that scanned (status = scanned). */
  scanned_by?: string | null;
  /** The login pair (status = approved; delivered exactly once). */
  login?: LoginResponse & { signal_link?: SignalLinkCredential | null };
}

/**
 * What the account's PRIMARY signed so this browser may register as a Signal
 * device (ADR-0027 D4.4). Absent when nothing vouched for it — an older phone
 * build, or a browser that declared no identity — and that absence is a state
 * to SHOW: signed in, and unable to read an encrypted chat.
 */
export interface SignalLinkCredential {
  device_number: number;
  linked_by_device_id: number;
  /** base64; the browser forwards it verbatim to `POST /e2e/devices`. */
  link_signature: string;
}

/** `POST /e2e/devices` — this browser publishing itself to the key directory.
 *  Public material only, plus the primary's signature over it. */
export interface E2eDeviceRegistration {
  registration_id: number;
  device_id: number;
  identity_key: string;
  signed_prekey_id: number;
  signed_prekey_public: string;
  signed_prekey_signature: string;
  kyber_prekey_id: number;
  kyber_prekey_public: string;
  kyber_prekey_signature: string;
  linked_by_device_id: number;
  link_signature: string;
}

/** The device row the directory hands back — `id` is what prekey uploads and
 *  the linked-devices screen address. */
export interface E2eDeviceSummary {
  id: string;
  device_id: number;
  registration_id: number;
}

/**
 * One of a PEER's active devices (`GET /e2e/peers/:user_id/devices`).
 *
 * `device_id` is the Signal device NUMBER — half of the `(name, deviceNumber)`
 * address the protocol works in — and `id` is the server's row uuid, which the
 * protocol never sees. The endpoint also carries the device's identity key and
 * link signature for a reconcile that must refuse an unvouched device; the
 * safety-number screen reads neither, and they are typed here so a reader is
 * not left thinking the wire is narrower than it is.
 */
export interface PeerDeviceRef {
  id: string;
  device_id: number;
  registration_id: number;
  linked_by_device_id?: string | null;
  /** base64 */
  link_signature?: string | null;
  /** base64 */
  identity_key?: string | null;
}

// --- mini-apps -------------------------------------------------------------

import type { MiniAppManifest } from "../runtime/manifest.ts";

/** What the embedded mini-app host launches: a validated manifest + the JS the
 *  isolate evaluates. On web this is the `logic` string of a compiled bundle. */
export interface MiniAppPackage {
  manifest: MiniAppManifest;
  code: string;
}

/** A picker row (mapped from a registry `App`: GET /miniapps/featured|installed). */
export interface MiniAppListItem {
  /** The id the bundle endpoint takes (the App PK, or the app_id slug). */
  id: string;
  app_id: string;
  name: string;
  description?: string | null;
  icon_url?: string | null;
  /** A first-party app resolved locally (no registry fetch). */
  builtin?: boolean;
}

/** The compiled-bundle envelope served by GET /miniapps/:id/bundle
 *  (miniapps/dsl/bundle.schema.json). The host only needs manifest + logic. */
export interface MiniAppBundleEnvelope {
  format: string;
  bundle_version: number;
  manifest: MiniAppManifest;
  logic: string;
  views?: Record<string, unknown>;
  entry?: string;
  handlers?: string[];
}

export const BUNDLE_FORMAT = "sema-miniapp-bundle";
export const SUPPORTED_BUNDLE_VERSION = 1;

// --- payments --------------------------------------------------------------

/** A mini-app's `payments.intent` request (the `sema.payments.requestPayment`
 *  args, mirrors KMP PaymentIntentRequest). The app states WHAT to charge; the
 *  host owns HOW — the user picks the provider + enters their number in a trusted
 *  sheet the app never sees. */
export interface PaymentIntentRequest {
  amount: number;
  description: string;
  currency: string;
  payee: string | null;
}

/** What the user approves in the host sheet — never returned to the mini-app. */
export interface PaymentApproval {
  provider: string;
  phone: string;
}

/** The result handed back to the mini-app (mirrors KMP PaymentIntentResult). */
export interface PaymentIntentResult {
  status: string;
  amount: number;
  currency: string;
  reference: string | null;
  provider: string | null;
}

/** POST /api/v1/payments/initiate response (Payments.payment_result). */
export interface PaymentChargeResult {
  transaction_id: string | null;
  status: string;
  provider_ref: string | null;
  message?: string | null;
  checkout_url?: string | null;
}

// --- Circle posts (D9, ADR-0024) --------------------------------------------

/**
 * One comment. There is no total anywhere near it: the list you are given IS
 * what you may see under the mutual-only rule, and a count would leak the
 * existence of the ones you may not.
 */
export interface PostComment {
  id: string;
  post_id: string;
  author_id: string;
  author?: StatusAuthor | null;
  reply_to_id?: string | null;
  body: string;
  created_at?: string | null;
}

/**
 * One post. A superset of `StatusItem`, because a post IS a status row with no
 * deadline (A1) — `audience`, `comment_policy` and `comments` are the only keys
 * a status does not have.
 */
export interface CirclePost {
  id: string;
  author_id: string;
  author?: StatusAuthor | null;
  kind: string;
  posted_at?: string | null;
  audience: string;
  comment_policy: string;
  caption?: string | null;
  text_bg?: string | null;
  renditions?: string[];
  liked?: boolean;
  comments?: PostComment[];
  /**
   * ADR-0029 D13. **Absent on anything that is not a public post**, and the
   * absence is the contract: a circle post carries no `counts` key at all, and
   * the renderer must draw no scoreboard when it is missing. Optional rather
   * than defaulted to zeroes, so "there is no scoreboard" and "the scoreboard
   * reads zero" stay different facts on the wire as well as in the UI.
   */
  counts?: PostCounts | null;
  reposted?: boolean;
  saved?: boolean;
  quoted?: QuotedPost | null;
  thread?: ThreadPosition | null;
}

/** The public numbers on a public post (ADR-0029 D13). Duniani only. */
export interface PostCounts {
  likes: number;
  replies: number;
  reposts: number;
  quotes: number;
}

/** The post a quote points at. `gone` is a real state: the quoter's words
 *  outlive the original being deleted, and a tombstone says which happened. */
export interface QuotedPost {
  id: string;
  author?: StatusAuthor | null;
  caption?: string | null;
  gone?: boolean;
}

/** "3 of 5" in a numbered series (ADR-0029 D17). Derived server-side. */
export interface ThreadPosition {
  index: number;
  of: number;
}

/** One entry in the Activity tab (ADR-0029 D17). */
export interface ActivityEvent {
  kind: "reply" | "quote" | "repost" | "follow";
  actor_id: string;
  actor?: StatusAuthor | null;
  post_id?: string | null;
  at?: string | null;
  body?: string | null;
}

// --- Channels (D6, ADR-0024) ------------------------------------------------

/**
 * One channel row.
 *
 * What separates this from a circle post is WHO CAN REPLY: a Moment takes
 * comments from mutual contacts, a channel post takes none at all, and
 * Mazungumzo (unbuilt) takes them from anyone. Different types rather than a
 * flag, because a broadcast with a comment thread is a different product and
 * modelling them as one shape is how that difference gets lost in an `if`.
 */
/**
 * One row of the channel DIRECTORY (ADR-0024 A16) — Explore's shape.
 *
 * A separate type from `Channel` on purpose: a Channel is a SUBSCRIPTION whose
 * interesting facts are what is new and whether it is muted; a listing is a
 * SEARCH RESULT whose interesting facts are what this publisher is and whether
 * you already have it. Folding them together would give every followed channel
 * a `following` flag that is true by construction and a `description` the list
 * endpoint does not send.
 *
 * No follower count, and here that is stronger than "withheld": the directory
 * is alphabetical, so the count never enters the server's query.
 */
export interface ChannelListing {
  id: string;
  name?: string | null;
  kind?: string | null;
  description?: string | null;
  following?: boolean;
}

/**
 * One person, as a follow list returns them (D12, ADR-0025).
 *
 * The same triple every other reader of a person gets. There is no
 * `follower_count` here and there must never be one: a follower count is the
 * scoreboard that turns a conversation into a performance, and it is refused
 * before anything is built rather than after somebody asks.
 */
export interface WirePerson {
  user_id: string;
  username?: string | null;
  display_name?: string | null;
  avatar?: unknown;
  frame?: string | null;
}

/**
 * A publisher this user OWNS — Office's shape (ADR-0024 D6/D7, Phase 3).
 *
 * A third channel-ish type on purpose. `Channel` is a SUBSCRIPTION, and
 * `ChannelListing` is a SEARCH RESULT; this is an ACCOUNT I RUN, and its only
 * two interesting facts are what it is called and whether it may publish.
 * `publishes` is meaningless to a follower and `unread` is meaningless about
 * yourself, so folding them together gives a type whose fields are only
 * sometimes meant.
 *
 * `publishes` was missing from the API's own JSON encoder until s138: the
 * toggle endpoint named it in its own reply, so the write looked right and
 * every other read of the account was silent about it.
 */
/**
 * An organisation (*Shirika*) — ADR-0018 step 1, Office's second section.
 *
 * `role` is the VIEWER's own role, not a property of the organisation: it
 * decides which controls get drawn, and a second person reading the same
 * organisation gets a different value here.
 *
 * `zone_count` and `member_count` are nullable because the zone endpoint
 * carries the organisation WITHOUT them — a zone header needs the name and the
 * viewer's role, not a tally it would not draw. A `0` default there would have
 * the screen state as fact something the server never sent.
 */
export interface Organisation {
  id: string;
  name: string;
  slug: string;
  country?: string | null;
  role: string;
  zone_count?: number | null;
  member_count?: number | null;
}

export interface OrganisationMember {
  user_id: string;
  username?: string | null;
  display_name?: string | null;
  role: string;
  avatar?: WireAvatar | null;
  frame?: string | null;
}

/** A zone's settings row. The conversation itself is a `Chat` — a zone IS one. */
export interface Zone {
  chat_id: string;
  org_id: string;
  kind: string;
  /** ADR-0037 E2 — whether the organisation's assistant is on here and the
   *  shape it wears. Its instructions are the managers', on `AssistantZone`. */
  assistant_enabled?: boolean;
  assistant_shape?: string | null;
}

/** An organisation's assistant (ADR-0037 E1) — not a person: no phone, never
 *  signs in, seated only where a manager turns it on. */
export interface OrgAssistant {
  user_id: string;
  display_name?: string | null;
}

/** One zone as the managers see its assistant settings, instructions included. */
export interface AssistantZone {
  chat_id: string;
  name?: string | null;
  kind: string;
  assistant_enabled: boolean;
  assistant_shape?: string | null;
  assistant_instructions?: string | null;
}

/** `GET /office/organisations/:id/assistant`. `assistant` is null until
 *  somebody adds one. */
export interface AssistantSetup {
  assistant: OrgAssistant | null;
  zones: AssistantZone[];
}

/** An organisation API key as the owner's list shows it (ADR-0037 A) — never
 *  the secret, which exists once, in the issue answer, and nowhere after. */
export interface OrgApiKey {
  id: string;
  name: string;
  key_prefix: string;
  scopes: string[];
  created_at?: string | null;
  expires_at?: string | null;
  last_used_at?: string | null;
  revoked_at?: string | null;
  refused_count?: number;
  last_refused_at?: string | null;
}

export interface Publisher {
  id: string;
  name: string;
  display_name?: string | null;
  description?: string | null;
  kind?: string | null;
  category?: string | null;
  publishes?: boolean;
  /**
   * Standing — READ-ONLY, and it must stay that way. It was castable from a
   * create request until s138, which made ADR-0024 A15's defence against
   * ministry impersonation something the impersonator could award itself.
   */
  account_type?: string | null;
  /** `approved` is the server's own word. The old business list tested for
   *  "verified", which the schema's whitelist does not contain, so the tick it
   *  drew could never appear on any account. */
  verification_status?: string | null;
}

export interface Channel {
  id: string;
  name?: string | null;
  /** The publisher kind as a RAW WIRE STRING, never a union: the server serves
   *  the list (`GET /channels/kinds`) precisely so a client cannot hold a stale
   *  copy, and a value this client has never heard of must render, not throw. */
  kind?: string | null;
  /** Mute means "stop waking me", never "hide it from me" — a muted channel
   *  keeps its row and reports 0 unread. */
  muted?: boolean;
  /** Posts THIS READER has not read: the same fact a chat badge carries, about
   *  the reader and never about the author. */
  unread?: number;
  latest?: ChannelPost | null;
  // ADR-0036 — the header's facts, served by `GET /channels/:id` only.
  description?: string | null;
  avatar?: string | null;
  verified?: boolean;
  /** The WORLD half's number (ADR-0029 D13): drawn on a channel, never on a
   *  person. */
  followers?: number;
  following?: boolean;
  publishes?: boolean;
}

/** One emoji and how many readers chose it (ADR-0036). */
export interface ReactionCount {
  emoji: string;
  count: number;
}

export interface ChannelPost {
  id: string;
  channel_id?: string | null;
  kind: string;
  caption?: string | null;
  posted_at?: string | null;
  media_bytes?: number | null;
  thumb_bytes?: number | null;
  duration_ms?: number | null;
  width?: number | null;
  height?: number | null;
  /** Which renditions exist on the STATUS byte route — empty for a text post,
   *  and missing from the wire until ADR-0036, which is why every client drew
   *  a channel photo as the word "Photo". */
  renditions?: string[];
  /** The public aggregate: the top emojis, the total, and this reader's own.
   *  A reaction reply carries the same three keys, so one mapper updates a row
   *  from either. */
  reactions?: ReactionCount[];
  reaction_total?: number;
  my_reaction?: string | null;
}
